(() => {
  const MOBILE_QUERY = '(max-width: 989px)';
  const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const slugify = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'article-section';

  const prefersReducedMotion = () => window.matchMedia(MOTION_QUERY).matches;

  const scrollOffsetFor = (element) => {
    const value = parseFloat(window.getComputedStyle(element).scrollMarginTop);
    return Number.isFinite(value) ? value : 0;
  };

  const initLayout = (root) => {
    if (!root || root.dataset.articleLayout3Ready === 'true') return;
    root.dataset.articleLayout3Ready = 'true';

    const tocColumn = root.querySelector('[data-article-toc]');
    const tocList = root.querySelector('[data-article-toc-list]');
    const articleContent = root.querySelector('[data-article-content]');
    const toggle = root.querySelector('[data-article-toc-toggle]');
    const panel = root.querySelector('[data-article-toc-panel]');

    if (!tocColumn || !tocList || !articleContent) return;

    const selectors = root.dataset.tocLevels || 'h2,h3';
    const headings = Array.from(articleContent.querySelectorAll(selectors)).filter(
      (heading) => heading.textContent.trim().length > 0
    );

    if (!headings.length) {
      tocColumn.classList.add('is-empty');
      root.classList.add('is-toc-empty');
      return;
    }

    const mobile = window.matchMedia(MOBILE_QUERY);
    const cleanups = [];
    const usedIds = new Set(Array.from(document.querySelectorAll('[id]')).map((node) => node.id));
    const links = new Map();
    let sectionNumber = 0;
    let subsectionNumber = 0;

    /* ---------------------------------------------------------------
       Mobile accordion. The panel is animated by setting an explicit
       max-height and then releasing it, so the list never becomes a
       second scrolling box inside the page.
    --------------------------------------------------------------- */

    const isOpen = () => Boolean(panel && panel.classList.contains('is-open'));

    const releaseHeight = () => {
      if (panel && isOpen()) panel.style.maxHeight = 'none';
    };

    const setPanelState = (open, { instant = false } = {}) => {
      if (!toggle || !panel) return;

      const animate = !instant && !prefersReducedMotion();

      if (instant) {
        panel.style.transition = 'none';
      }

      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (open) {
        panel.classList.add('is-open');
        panel.style.maxHeight = animate ? `${panel.scrollHeight}px` : 'none';
      } else {
        // Lock the current height first so the collapse has something to animate from.
        panel.style.maxHeight = `${panel.scrollHeight}px`;
        void panel.offsetHeight;
        panel.classList.remove('is-open');
        panel.style.maxHeight = '0px';
      }

      if (instant) {
        void panel.offsetHeight;
        panel.style.transition = '';
      }
    };

    const resetPanelForDesktop = () => {
      if (!panel) return;
      panel.style.maxHeight = '';
      panel.style.transition = '';
    };

    if (toggle && panel) {
      const startOpen = root.dataset.tocMobileOpen === 'true';

      if (mobile.matches) {
        setPanelState(startOpen, { instant: true });
      } else {
        toggle.setAttribute('aria-expanded', startOpen ? 'true' : 'false');
        resetPanelForDesktop();
      }

      const onToggleClick = () => setPanelState(!isOpen());
      toggle.addEventListener('click', onToggleClick);
      cleanups.push(() => toggle.removeEventListener('click', onToggleClick));

      const onTransitionEnd = (event) => {
        if (event.target === panel && event.propertyName === 'max-height') releaseHeight();
      };
      panel.addEventListener('transitionend', onTransitionEnd);
      cleanups.push(() => panel.removeEventListener('transitionend', onTransitionEnd));
    }

    /* ---------------------------------------------------------------
       Build the list
    --------------------------------------------------------------- */

    const scrollToHeading = (heading) => {
      const target =
        heading.getBoundingClientRect().top + window.pageYOffset - scrollOffsetFor(heading);

      window.scrollTo({
        top: Math.max(target, 0),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    };

    headings.forEach((heading, index) => {
      const isSubitem = heading.tagName.toLowerCase() === 'h3';

      if (isSubitem) {
        if (sectionNumber === 0) sectionNumber = 1;
        subsectionNumber += 1;
      } else {
        sectionNumber += 1;
        subsectionNumber = 0;
      }

      if (!heading.id) {
        const baseId = slugify(heading.textContent);
        let uniqueId = baseId;
        let suffix = 2;

        while (usedIds.has(uniqueId)) {
          uniqueId = `${baseId}-${suffix}`;
          suffix += 1;
        }

        heading.id = uniqueId;
      }

      usedIds.add(heading.id);

      const item = document.createElement('li');
      item.className = `m-article-l3__toc-item${isSubitem ? ' is-subitem' : ''}`;

      const link = document.createElement('a');
      link.className = 'm-article-l3__toc-link';
      link.href = `#${heading.id}`;

      const number = document.createElement('span');
      number.className = 'm-article-l3__toc-number';
      number.textContent = isSubitem ? `${sectionNumber}.${subsectionNumber}` : `${sectionNumber}.`;

      const label = document.createElement('span');
      label.className = 'm-article-l3__toc-label';
      label.textContent = heading.textContent.trim();

      link.append(number, label);
      item.appendChild(link);
      tocList.appendChild(item);
      links.set(heading.id, link);

      link.addEventListener('click', (event) => {
        event.preventDefault();

        // On phones the open accordion sits above the article, so collapse it
        // without animation first — otherwise the target drifts while we scroll.
        if (mobile.matches && isOpen()) {
          setPanelState(false, { instant: true });
        }

        window.requestAnimationFrame(() => scrollToHeading(heading));

        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', `#${heading.id}`);
        }
      });

      if (index === 0) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'location');
      }
    });

    /* ---------------------------------------------------------------
       Active link tracking
    --------------------------------------------------------------- */

    const setActive = (id) => {
      links.forEach((link, linkId) => {
        const active = linkId === id;
        link.classList.toggle('is-active', active);
        if (active) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    let ticking = false;

    const updateActive = () => {
      ticking = false;
      const threshold = scrollOffsetFor(headings[0]) + 24;
      let activeHeading = headings[0];

      headings.forEach((heading) => {
        if (heading.getBoundingClientRect().top <= threshold) activeHeading = heading;
      });

      // Bottom of the page: last section should read as current.
      if (window.innerHeight + window.pageYOffset >= document.body.scrollHeight - 4) {
        activeHeading = headings[headings.length - 1];
      }

      setActive(activeHeading.id);
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActive);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', requestUpdate));

    let resizeTimer = null;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (!mobile.matches) resetPanelForDesktop();
        else releaseHeight();
        requestUpdate();
      }, 120);
    };

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });
    cleanups.push(() => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    });

    const onBreakpointChange = (event) => {
      if (event.matches) {
        setPanelState(root.dataset.tocMobileOpen === 'true', { instant: true });
      } else {
        resetPanelForDesktop();
      }
      requestUpdate();
    };

    if (typeof mobile.addEventListener === 'function') {
      mobile.addEventListener('change', onBreakpointChange);
      cleanups.push(() => mobile.removeEventListener('change', onBreakpointChange));
    }

    // Landing on the page with a #hash: browsers scroll before our ids exist.
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target && articleContent.contains(target)) {
        window.requestAnimationFrame(() => {
          window.scrollTo({
            top: Math.max(
              target.getBoundingClientRect().top + window.pageYOffset - scrollOffsetFor(target),
              0
            ),
            behavior: 'auto',
          });
          requestUpdate();
        });
      }
    }

    // Images loading late shift every heading position.
    articleContent.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', requestUpdate, { once: true });
      img.addEventListener('error', requestUpdate, { once: true });
    });

    updateActive();

    root.__articleLayout3Destroy = () => {
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
      delete root.dataset.articleLayout3Ready;
      delete root.__articleLayout3Destroy;
    };
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches('[data-article-layout3]')) initLayout(scope);
    scope.querySelectorAll?.('[data-article-layout3]').forEach(initLayout);
  };

  const destroyAll = (scope) => {
    scope.querySelectorAll?.('[data-article-layout3]').forEach((root) => {
      if (typeof root.__articleLayout3Destroy === 'function') root.__articleLayout3Destroy();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor: rebuild cleanly instead of stacking listeners on every save.
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
